import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import {
  TUTORIAL_NAME_POST,
  TUTORIAL_NAME_SEARCH,
  TUTORIAL_NAME_TOPIC,
} from '../../constants/onboarding';
import searchIcon from '../../icons/non-prefilled/16x16/search.svg';
import PostIcon from '../../post/PostIcon';
import TopicIcon from '../../topic/TopicIcon';
import topicPolicyCheck, {
  TopicPolicyAction,
} from '../../topic/topicPolicyCheck';
import useAllTopics from '../../topic/useAllTopics';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import topicPath from '../../utils/url/topicPath';
import type { TutorialName } from '../onboardingSlice';
import styles from './AvailableTutorials.module.scss';
import type { UserType } from '../../graphql/generated/graphql';

interface Tutorial {
  displayName: string;
  name: TutorialName;
  icon: React.ReactNode;
  link?: string;
}

interface AvailableTutorialsProps {
  children: (tutorials: Tutorial[]) => React.ReactNode;
}

const canShowTutorial = (
  topic: Parameters<typeof topicPolicyCheck>[1],
  user: Parameters<typeof topicPolicyCheck>[2] & { type?: UserType | null },
) =>
  topicPolicyCheck(TopicPolicyAction.EditTopic, topic, user) ||
  ((['OPEN', 'PUBLIC'].includes(topic.privacy) || user.type === 'ADMIN') &&
    topic.memberEditable);

const useTopicTutorialPath = (topicId?: string) => {
  const user = useAppSelector((state) => state.session.user);
  const topics = useAllTopics({ flatten: true });
  const currentTopic = topicId && topics.find(({ id }) => id === topicId);
  const topic =
    currentTopic && canShowTutorial(currentTopic, user)
      ? currentTopic
      : topics.find((t) => canShowTutorial(t, user));

  return topic ? topicPath(topic) : null;
};

const AvailableTutorials = ({ children }: AvailableTutorialsProps) => {
  const [topicId] = useSlug('topicSlug');
  const postTutorialPath = createPostPath();
  const topicTutorialPath = useTopicTutorialPath(topicId);

  const tutorials: Tutorial[] = [
    {
      displayName: 'Post',
      name: TUTORIAL_NAME_POST,
      link: postTutorialPath,
      icon: <PostIcon />,
    },
  ];

  if (topicTutorialPath) {
    tutorials.push({
      displayName: 'Topic',
      name: TUTORIAL_NAME_TOPIC,
      link: topicTutorialPath,
      icon: <TopicIcon />,
    });
  }

  tutorials.push({
    displayName: 'Search',
    name: TUTORIAL_NAME_SEARCH,
    icon: (
      <div className={styles.searchIconContainer}>
        <SVGIcon>{searchIcon}</SVGIcon>
      </div>
    ),
  });

  return <>{children(tutorials)}</>;
};

export default AvailableTutorials;
